import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Grid,
  FormControlLabel,
  Switch,
  Typography,
  Box,
  IconButton,
  Alert
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { Size, SizeCreate, SizeUpdate, SizeParameterCreate } from '../types';
import { apiService } from '../services/api';
import LoadingButton from './LoadingButton';

interface SizeDialogProps {
  open: boolean;
  onClose: () => void;
  onSizeCreated?: () => void;
  onSizeUpdated?: () => void;
  size?: Size | null;
}

const SizeDialog: React.FC<SizeDialogProps> = ({
  open,
  onClose,
  onSizeCreated,
  onSizeUpdated,
  size
}) => {
  const { t } = useTranslation();
  const isEdit = !!size;

  const [formData, setFormData] = useState({
    name: '',
    display_name: '',
    description: '',
    is_active: true
  });

  const [parameters, setParameters] = useState<SizeParameterCreate[]>([
    { parameter_name: 'max_users', parameter_value: '5', description: t('sizes.defaultParameters.max_users') },
    { parameter_name: 'max_projects', parameter_value: '5', description: t('sizes.defaultParameters.max_projects') },
    { parameter_name: 'max_connections_per_project', parameter_value: '2', description: t('sizes.defaultParameters.max_connections_per_project') },
    { parameter_name: 'max_tables_per_connection', parameter_value: '20', description: t('sizes.defaultParameters.max_tables_per_connection') },
    { parameter_name: 'max_records_per_table', parameter_value: '500000', description: t('sizes.defaultParameters.max_records_per_table') }
  ]);

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (size) {
      setFormData({
        name: size.name,
        display_name: size.display_name,
        description: size.description || '',
        is_active: size.is_active
      });
      
      if (size.parameters && size.parameters.length > 0) {
        setParameters(size.parameters.map(p => ({
          parameter_name: p.parameter_name,
          parameter_value: p.parameter_value,
          description: p.description || ''
        })));
      }
    } else {
      // Reset form for create
      setFormData({
        name: '',
        display_name: '',
        description: '',
        is_active: true
      });
      setParameters([
        { parameter_name: 'max_users', parameter_value: '5', description: t('sizes.defaultParameters.max_users') },
        { parameter_name: 'max_projects', parameter_value: '5', description: t('sizes.defaultParameters.max_projects') },
        { parameter_name: 'max_connections_per_project', parameter_value: '2', description: t('sizes.defaultParameters.max_connections_per_project') },
        { parameter_name: 'max_tables_per_connection', parameter_value: '20', description: t('sizes.defaultParameters.max_tables_per_connection') },
        { parameter_name: 'max_records_per_table', parameter_value: '500000', description: t('sizes.defaultParameters.max_records_per_table') }
      ]);
    }
    setError(null);
  }, [size, t]);

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleParameterChange = (index: number, field: string, value: any) => {
    setParameters(prev => {
      const newParams = [...prev];
      newParams[index] = {
        ...newParams[index],
        [field]: value
      };
      return newParams;
    });
  };

  const addParameter = () => {
    setParameters(prev => [
      ...prev,
      { parameter_name: '', parameter_value: '', description: '' }
    ]);
  };

  const removeParameter = (index: number) => {
    setParameters(prev => prev.filter((_, i) => i !== index));
  };

  const validateForm = () => {
    if (!formData.name.trim()) {
      setError(t('sizes.validation.nameRequired'));
      return false;
    }

    if (!formData.display_name.trim()) {
      setError(t('sizes.validation.displayNameRequired'));
      return false;
    }

    for (const param of parameters) {
      if (!param.parameter_name.trim()) {
        setError(t('sizes.validation.parameterNameRequired'));
        return false;
      }
      if (!param.parameter_value || !param.parameter_value.toString().trim()) {
        setError(t('sizes.validation.parameterValueRequired'));
        return false;
      }
    }

    return true;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    try {
      setSubmitting(true);
      setError(null);

      if (isEdit && size) {
        // Update existing size (basic info only, parameters handled separately)
        const updateData: SizeUpdate = {
          name: formData.name,
          display_name: formData.display_name,
          description: formData.description,
          is_active: formData.is_active
        };
        
        await apiService.updateSize(size.id, updateData);
        onSizeUpdated?.();
      } else {
        // Create new size
        const createData: SizeCreate = {
          ...formData,
          parameters
        };
        
        await apiService.createSize(createData);
        onSizeCreated?.();
      }

      onClose();
    } catch (err: any) {
      console.error('Error saving size:', err);
      setError(err.detail || 'Error saving size');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {isEdit ? t('sizes.editSize') : t('sizes.createSize')}
      </DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid item xs={12} sm={6}>
            <TextField
              label={t('sizes.fields.name')}
              value={formData.name}
              onChange={(e) => handleInputChange('name', e.target.value)}
              fullWidth
              required
              disabled={isEdit} // Name shouldn't be changed after creation
              helperText={isEdit ? "Name cannot be changed after creation" : "Internal name (lowercase, no spaces)"}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label={t('sizes.fields.displayName')}
              value={formData.display_name}
              onChange={(e) => handleInputChange('display_name', e.target.value)}
              fullWidth
              required
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label={t('sizes.fields.description')}
              value={formData.description}
              onChange={(e) => handleInputChange('description', e.target.value)}
              fullWidth
              multiline
              rows={2}
            />
          </Grid>
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.is_active}
                  onChange={(e) => handleInputChange('is_active', e.target.checked)}
                />
              }
              label={t('sizes.fields.isActive')}
            />
          </Grid>
        </Grid>

        {!isEdit && (
          <>
            <Typography variant="h6" sx={{ mt: 3, mb: 2 }}>
              {t('sizes.parameters')}
            </Typography>

            {parameters.map((param, index) => (
              <Box key={index} sx={{ mb: 2, p: 2, border: '1px solid #e0e0e0', borderRadius: 1 }}>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} sm={4}>
                    <TextField
                      label={t('sizes.fields.parameterName')}
                      value={param.parameter_name}
                      onChange={(e) => handleParameterChange(index, 'parameter_name', e.target.value)}
                      fullWidth
                      required
                    />
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <TextField
                      label={t('sizes.fields.parameterValue')}
                      type="text"
                      value={param.parameter_value}
                      onChange={(e) => handleParameterChange(index, 'parameter_value', e.target.value)}
                      fullWidth
                      required
                      placeholder="e.g. 1000 or 250m"
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      label={t('sizes.fields.parameterDescription')}
                      value={param.description}
                      onChange={(e) => handleParameterChange(index, 'description', e.target.value)}
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={12} sm={1}>
                    <IconButton
                      onClick={() => removeParameter(index)}
                      color="error"
                      disabled={parameters.length === 1}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Grid>
                </Grid>
              </Box>
            ))}

            <Button
              startIcon={<AddIcon />}
              onClick={addParameter}
              variant="outlined"
              sx={{ mt: 1 }}
            >
              Add Parameter
            </Button>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>
          {t('common.cancel')}
        </Button>
        <LoadingButton
          onClick={handleSubmit}
          loading={submitting}
          variant="contained"
        >
          {isEdit ? t('common.update') : t('common.create')}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};

export default SizeDialog;
